export const NAVBAR_SCROLL_THRESHOLD = 80
export const NAVBAR_TOUCH_THRESHOLD = 8

// Turns scroll and touch movement into one stable visibility contract. A document scroll is
// enough for a mouse, trackpad or keyboard. On a touchscreen the finger itself is authoritative:
// at the end of the document `scrollY` cannot move any farther, but the user still expects an
// upward swipe to put the compact surface back on top.
export class NavbarVisibility {
  constructor(scrollY = 0) {
    this.lastScroll = scrollY
    this.touchPoint = null
    this.surfaceLocked = false
    this.playerAvailable = false
  }

  scroll(scrollY) {
    const previousScroll = this.lastScroll
    this.lastScroll = scrollY

    // Showing or resizing the player can make the browser adjust scrollY through scroll
    // anchoring. An explicit surface choice survives that layout movement until the person
    // supplies a real navigation gesture.
    if (this.surfaceLocked) return null
    if (this.touchPoint) return null
    if (scrollY === 0 && previousScroll > 0) return "show"
    if (scrollY > previousScroll && scrollY > NAVBAR_SCROLL_THRESHOLD) return "hide"
    if (scrollY < previousScroll) return "show"

    return null
  }

  touchStart(point, scrollY) {
    this.unlock(scrollY)
    this.touchPoint = point
  }

  touchMove(point, scrollY) {
    if (!this.touchPoint) return null

    const deltaX = point.x - this.touchPoint.x
    const deltaY = point.y - this.touchPoint.y
    this.lastScroll = scrollY

    // Do not turn a tap, jitter or horizontal seek on the player into a header change.
    if (Math.abs(deltaY) < NAVBAR_TOUCH_THRESHOLD || Math.abs(deltaY) <= Math.abs(deltaX)) {
      return null
    }

    // Measure the next change from the point that produced this decision. Reversing a swipe
    // therefore reverses the visible surface promptly instead of having to cross its origin.
    this.touchPoint = point

    if (deltaY > 0) return "show"
    if (this.playerAvailable || scrollY > NAVBAR_SCROLL_THRESHOLD) return "hide"

    return null
  }

  touchEnd(scrollY) {
    this.touchPoint = null
    this.lastScroll = scrollY
  }

  navigate(deltaY, scrollY) {
    this.unlock(scrollY)

    // At either edge of the document there may be no subsequent scroll event. Use the person's
    // wheel or keyboard direction directly so an upward gesture always reveals navigation.
    if (deltaY < 0) return "show"
    if (deltaY > 0 && (this.playerAvailable || scrollY > NAVBAR_SCROLL_THRESHOLD)) return "hide"

    return null
  }

  select(surface, scrollY) {
    this.touchPoint = null
    this.lastScroll = scrollY
    this.surfaceLocked = true
    return surface
  }

  playerPresented(scrollY) {
    this.playerAvailable = true
    return this.select("hide", scrollY)
  }

  playerDismissed(scrollY) {
    this.playerAvailable = false
    return this.select("show", scrollY)
  }

  unlock(scrollY) {
    this.lastScroll = scrollY
    this.surfaceLocked = false
  }
}
